// src/components/Chatbot.tsx

import React, { useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ChatWindowState } from '../App';
import RuntimeValuesProvider, { RuntimeValuesContext } from '../context/RuntimeValuesContext';
import {useTranslation} from 'react-i18next';
import config from '../config/config.json';
import { run } from 'node:test';

interface Product {
    id: number;
    name: string;
    priceCurrency: number;
    productBrand: string;
}

interface Message {
  id: number;
  sender: 'user' | 'bot';
  text?: string;         // Will hold the main AI summary
  products?: Product[];
  introduction?: string; // <-- ADDED for the intro text
  followUp?: string;     // <-- ADDED for the final question
}

interface ChatbotProps {
    chatState: ChatWindowState;
    setChatState: (state: ChatWindowState) => void;
}

interface ChatbotMessages {
  initialMessage: string;
}

const Chatbot: React.FC<ChatbotProps> = ({ chatState, setChatState }) => {
  const [t, i18n] = useTranslation("global");            
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const {runtimeValues, setRuntimeValues} = useContext(RuntimeValuesContext);

    useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    setChatState('closed');
    switch(runtimeValues.language){
      case "ESPAÑOL":{
        setMessages([{
            id: 1,
            sender: 'bot',
            text: 'Hola! Soy el asistente IA de WICA. Pregúntame acerca de nuestros productos, como "Tienes cámaras RED?"'
        }]);
      }break;
      default:{
        setMessages([{
            id: 1,
            sender: 'bot',
            text: `Hi there! I am WICA's AI Assistant. Ask me about our products, like "do you have RED cameras?"`
        }]);
      }break;
    }    

  }, [runtimeValues.language]);

  useEffect(() => {
    if (chatState !== 'closed' && messages.length === 0) {
    switch(runtimeValues.language){
      case "ESPAÑOL":{
        setMessages([{
            id: 1,
            sender: 'bot',
            text: `Hola! Soy el asistente IA de WICA. Pregúntame acerca de nuestros productos, como "Tienes cámaras RED?"`
        }]);
      }break;
      default:{
        setMessages([{
            id: 1,
            sender: 'bot',
            text: `Hi there! I am WICA's AI Assistant. Ask me about our products, like "do you have RED cameras?"`
        }]);
      }break;
    } 
    }
  }, [chatState, messages.length]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isLoading) return;

    const userMessage: Message = { id: Date.now(), text: trimmedInput, sender: 'user' };
    const conversationHistory = messages.slice(-4); // Send the last 4 messages as context

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // vvvvvvvvvv START: ADD THESE LINES vvvvvvvvvv
      // Simplify the history to only include what the backend needs.
      // This prevents JSON serialization errors with complex objects.
      const simplifiedHistory = conversationHistory.map(msg => ({
        sender: msg.sender,
        text: msg.text || '' // Ensure text is always a string, even if undefined
      }));
      // ^^^^^^^^^^ END: ADD THESE LINES ^^^^^^^^^^

      // Now, send the SIMPLIFIED history to the backend
      const response = await axios.post(`${config.backend.url}/api/chatbot/`, { 
        query: trimmedInput, 
        language: runtimeValues.language,
        history: simplifiedHistory // <-- Use the new, clean variable
      });
      
      const data = response.data;

      const newBotMessage: Message = {
        id: Date.now(),
        sender: 'bot',
        introduction: data.introduction,
        text: data.answer,
        products: data.products,
        followUp: data.followUp
      };

      setMessages(prev => [...prev, newBotMessage]);

      //const botMessages: Message[] = [];
      //if (response.data.answer) {
      //  botMessages.push({ id: Date.now(), sender: 'bot', text: response.data.answer });
      //}
      //if (response.data.products && response.data.products.length > 0) {
      //  botMessages.push({ id: Date.now() + 1, sender: 'bot', products: response.data.products });
      //}
      //if (botMessages.length === 0) {
      //switch(runtimeValues.language){
      //  case "ESPAÑOL":{
      //     botMessages.push({ id: Date.now(), sender: 'bot', text: "Lo siento, no estoy seguro de cómo responder a eso." });
      //  }break;
      //  default:{
      //    botMessages.push({ id: Date.now(), sender: 'bot', text: "Sorry, I'm not sure how to respond to that." });
      //  }break;
      //}             
 
      //}
      //setMessages(prev => [...prev, ...botMessages]);
    } catch (error) {
      let errorMessage: Message;
      switch(runtimeValues.language){
        case "ESPAÑOL":{
           errorMessage = { id: Date.now() + 1, sender: 'bot', text: 'Parece que estoy teniendo problemas de conexión.' };
        }break;
        default:{
          errorMessage = { id: Date.now() + 1, sender: 'bot', text: 'I seem to be having connection issues.' };
        }break;
      }       
      console.error("Error communicating with chatbot API:", error);
      //const errorMessage: Message = { id: Date.now() + 1, sender: 'bot', text: 'I seem to be having connection issues.' };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const getWindowClass = () => {
    if (chatState === 'regular') return 'chat-window docked';
    let classes = 'chat-window floating';
    if (chatState === 'minimized') classes += ' minimized';
    if (chatState === 'maximized') classes += ' maximized';
    return classes;
  };

  const ChatIcon = () => ( <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg> );
  const SendIcon = () => ( <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"></path></svg> );
  const DockIcon = () => ( <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path><polyline points="13 2 13 9 20 9"></polyline></svg> );
  const UndockIcon = () => ( <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg> );
  const MaximizeIcon = () => ( <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path></svg> );
  const RestoreIcon = () => ( <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path></svg> );

  if (chatState === 'closed') {
    return (
        <button onClick={() => setChatState('floating')} className="chatbot-trigger" aria-label="Open Chat">
          <ChatIcon />
        </button>
    );
  }

  return (
    <div className={getWindowClass()}>
      <div className="chat-header">
          {runtimeValues.language === "ESPAÑOL"
              ?<h2>WICA - Asistente IA</h2>            
              :<h2>WICA - AI Assistant</h2>
          }
        <div className="chat-header-controls">
            {chatState === 'regular' 
                ? <button className="control-btn" onClick={() => setChatState('floating')} aria-label="Float Window"><UndockIcon /></button>
                : <button className="control-btn" onClick={() => setChatState('regular')} aria-label="Dock Window"><DockIcon /></button>
            }
            {chatState !== 'regular' && (
                <>
                <button className="control-btn" onClick={() => setChatState('minimized')} aria-label="Minimize" style={{fontWeight: 'bolder'}}>-</button>
                {chatState === 'maximized'
                    ? <button className="control-btn" onClick={() => setChatState('floating')} aria-label="Restore"><RestoreIcon /></button>
                    : <button className="control-btn" onClick={() => setChatState('maximized')} aria-label="Maximize"><MaximizeIcon /></button>
                }
                </>
            )}
            <button className="control-btn" onClick={() => setChatState('closed')} aria-label="Close" style={{fontSize: '1.5rem', fontWeight:'normal'}}>×</button>
        </div>
      </div>
      <div className="chat-messages">
        {messages.map(msg => (
          <div key={msg.id} className={`message-container ${msg.sender}-container`}>
            {/* For bot messages, we now render a single bubble with structured content */}
            {msg.sender === 'bot' ? (
              <div className="message-bubble bot-message">
                {/* 1. Render introduction */}
                {msg.introduction && <p>{msg.introduction}</p>}
                
                {/* 2. Render main text/summary */}
                {msg.text && <p>{msg.text}</p>}

                {/* 3. Render product list */}
                {msg.products && msg.products.length > 0 && (
                  <div className="products-list-container">
                    <ul>{msg.products.map((product) => (
                      <Link to={`/productDetail?productId=${product.id}`} key={product.id} className="product-link" onClick={() => setChatState('closed')}>
                        <li>
                          <span className="product-name">{product.name}</span>
                          <span className="product-price">${product.priceCurrency.toFixed(2)} USD</span>
                        </li>
                      </Link>
                    ))}</ul>
                  </div>
                )}

                {/* 4. Render follow-up */}
                {msg.followUp && <p>{msg.followUp}</p>}
              </div>
            ) : (
              // User messages are rendered simply as before
              <div className="message-bubble user-message">
                {msg.text}
              </div>
            )}
          </div>
        ))}
      </div>
      
      <form onSubmit={handleSendMessage} className="chat-input-form">
        <input type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)} placeholder="Ask about a product..." className="chat-input" disabled={isLoading} autoFocus />
        <button type="submit" className="chat-send-btn" disabled={isLoading || !inputValue.trim()} aria-label="Send Message"><SendIcon /></button>
      </form>
    </div>
  );
};

export default Chatbot;